import React, { useMemo, useRef, useState } from 'react';
import { downloadRespondentsCsv, errorMessage, listRespondents, listResponseStatuses } from '../../api';
import { useAsyncData } from '../../hooks/useAsyncData';
import { saveBlob } from '../../lib/saveBlob';
import type { Project, ReconcileResult, Respondent, RespondentPage, ResponseStatusInfo } from '../../types';
import { Badge, Button, Card, CardBody, EmptyState, ErrorState, InlineAlert, Pagination, SelectField, Skeleton, Table, Td, TextField, Th, useToast } from '../../ui';
import { browserTimeZone, datesOutOfOrder } from '../reports/reportsModel';
import { ReconcileDialog } from './ReconcileDialog';
import { STATUS_LABEL, STATUS_TONE, formatDateTime, statusKeyOf, statusLabel } from './respondentStatus';

const PAGE_SIZE = 50;

/**
 * The Respondents tab of a campaign: who entered the survey and how it ended (GET /project/{id}/respondents).
 * It is loaded only when the tab is opened (the list is paged and can be thousands of rows), filtered by status and target
 * group, and can be exported as CSV with the same filters. Respondents that finished in under 60 seconds are highlighted so
 * they are easy to select and mark as poor quality (reconciliation, which asks for confirmation first).
 */
export function RespondentsPanel({ project }: { project: Project }) {
  const { notify } = useToast();
  const [status, setStatus] = useState('');
  const [targetGroupId, setTargetGroupId] = useState('');
  // the days people entered (YYYY-MM-DD, both inclusive), cut in the browser's time zone; the list and the CSV share them
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const tz = browserTimeZone();
  const outOfOrder = datesOutOfOrder(from, to);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [openHistory, setOpenHistory] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const exportingRef = useRef(false);

  const list = useAsyncData<RespondentPage>((signal) => listRespondents(project.id, { status, targetGroupId, from, to, tz, page, size: PAGE_SIZE }, { signal }), [project.id, status, targetGroupId, from, to, tz, page], { enabled: !outOfOrder });
  const reference = useAsyncData<ResponseStatusInfo[]>((signal) => listResponseStatuses({ signal }), []);

  const groups = project.targetGroups || [];
  const groupName = (id?: string | null) => {
    const i = groups.findIndex((g) => g.id === id);
    return i < 0 ? '—' : groups[i].name || `Target group ${i + 1}`;
  };
  // Filter choices and descriptions come from the server's list; the built-in labels cover it if that call fails.
  const statusOptions = useMemo(() => {
    const keys = (reference.data || []).map(statusKeyOf).filter(Boolean);
    return keys.length ? keys : Object.keys(STATUS_LABEL);
  }, [reference.data]);
  const describe = (key: string) => {
    const info = (reference.data || []).find((x) => statusKeyOf(x) === key);
    return info && info.description ? info.description : undefined;
  };

  const rows = (list.data && list.data.items) || [];
  const chosen = rows.filter((r) => selected.has(r.id));
  const suspicious = rows.filter((r) => r.suspicious);

  const changeFilter = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setter(e.target.value);
    setPage(1);
    setSelected(new Set());
  };
  const changeDate = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setPage(1);
    setSelected(new Set());
  };
  const changePage = (p: number) => { setPage(p); setSelected(new Set()); };
  const toggle = (id: string) => setSelected((s) => { const next = new Set(s); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const allSelected = rows.length > 0 && chosen.length === rows.length;

  const exportCsv = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setExporting(true);
    try {
      const { blob, filename } = await downloadRespondentsCsv(project.id, { status, targetGroupId, from, to, tz }); // the current filters
      saveBlob(blob, filename || `respondents-${project.id}.csv`); // the server's name when it sends one
    } catch (err) {
      notify(errorMessage(err), { tone: 'danger', duration: 7000 });
    } finally {
      exportingRef.current = false;
      setExporting(false);
    }
  };

  const reconciled = (result: ReconcileResult) => {
    if (result.updated > 0) setSelected(new Set());
    list.reload();
  };

  let body: React.ReactNode;
  if (list.error) {
    body = <ErrorState title="Could not load respondents" message={errorMessage(list.error)} onRetry={list.reload} />;
  } else if (!list.data) {
    body = <div className="space-y-2 p-5" aria-busy="true">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>;
  } else if (rows.length === 0) {
    body = (
      <EmptyState
        title={status || targetGroupId || from || to ? 'No respondents match these filters' : 'No respondents yet'}
        description={status || targetGroupId || from || to ? 'Try another status, target group or period.' : 'Respondents appear here once panelists open the campaign\'s entry link.'}
      />
    );
  } else {
    body = (
      <>
        <Table caption="Respondents">
          <thead>
            <tr>
              <Th className="w-10"><input type="checkbox" aria-label="Select all respondents on this page" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)))} /></Th>
              <Th>Respondent</Th><Th>Target group</Th><Th>Panelist</Th><Th>Status</Th><Th>Started</Th><Th numeric>Length</Th><Th><span className="sr-only">History</span></Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r: Respondent) => (
              <React.Fragment key={r.id}>
                <tr className={r.suspicious ? 'bg-warn-soft/40' : 'hover:bg-canvas'}>
                  <Td><input type="checkbox" aria-label={`Select respondent ${r.id.slice(0, 8)}`} checked={selected.has(r.id)} onChange={() => toggle(r.id)} /></Td>
                  <Td className="font-mono text-xs" title={r.id}>{r.id.slice(0, 8)}</Td>
                  <Td className="text-muted">{groupName(r.targetGroupId)}</Td>
                  <Td className="font-mono text-xs text-muted">{r.panelistId || '—'}</Td>
                  <Td>
                    <span className="inline-flex flex-wrap items-center gap-1.5">
                      <span title={describe(r.status)}><Badge tone={STATUS_TONE[r.status] || 'neutral'}>{statusLabel(r.status)}</Badge></span>
                      {r.reconciled && <span className="text-xs text-muted">changed by hand</span>}
                      {r.suspicious && <Badge tone="warn">Under 60 s</Badge>}
                    </span>
                    {r.reason && <span className="mt-0.5 block text-xs text-muted">{r.reason}</span>}
                  </Td>
                  <Td className="whitespace-nowrap text-muted">{formatDateTime(r.startedAt)}</Td>
                  <Td numeric className="tabular-nums text-muted">{typeof r.loiMinutes === 'number' ? `${r.loiMinutes} min` : '—'}</Td>
                  <Td className="text-right">
                    {(r.history || []).length > 0 && (
                      <button type="button" aria-expanded={openHistory === r.id} aria-label={`History of respondent ${r.id.slice(0, 8)}`} onClick={() => setOpenHistory(openHistory === r.id ? null : r.id)} className="text-sm font-semibold text-brand-700 hover:underline">History</button>
                    )}
                  </Td>
                </tr>
                {openHistory === r.id && (
                  <tr>
                    <td colSpan={8} className="bg-canvas px-6 py-3">
                      <ol aria-label={`History of respondent ${r.id.slice(0, 8)}`} className="space-y-1 text-xs text-muted">
                        {(r.history || []).map((h, i) => (
                          <li key={`${h.at}-${i}`}>
                            {formatDateTime(h.at)} · <b className="text-ink">{statusLabel(h.status)}</b>{h.from ? ` (was ${statusLabel(h.from).toLowerCase()})` : ''}{h.source ? ` · ${h.source}` : ''}{h.reason ? ` · ${h.reason}` : ''}
                          </li>
                        ))}
                      </ol>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
        <Pagination page={page} pageSize={PAGE_SIZE} total={list.data.total} onPageChange={changePage} disabled={list.loading} />
      </>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardBody>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <SelectField label="Status" value={status} onChange={changeFilter(setStatus)}>
                <option value="">All statuses</option>
                {statusOptions.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
              </SelectField>
              <TextField label="Entered from" type="date" value={from} max={to || undefined} onChange={changeDate(setFrom)} />
              <TextField label="Entered to" type="date" value={to} min={from || undefined} onChange={changeDate(setTo)} error={outOfOrder ? 'Not before the first day' : undefined} />
              <SelectField label="Target group" value={targetGroupId} onChange={changeFilter(setTargetGroupId)}>
                <option value="">All target groups</option>
                {groups.map((g, i) => <option key={g.id} value={g.id}>{g.name || `Target group ${i + 1}`}</option>)}
              </SelectField>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="ghost" size="sm" onClick={list.reload} loading={list.loading && Boolean(list.data)}>Refresh</Button>
              <Button variant="secondary" size="sm" onClick={exportCsv} loading={exporting}>Download CSV</Button>
            </div>
          </div>
          {reference.error ? <div className="mt-3"><InlineAlert tone="info">The status descriptions could not be loaded; the built-in labels are shown.</InlineAlert></div> : null}
        </CardBody>
      </Card>

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3">
          <p className="text-sm text-muted">
            <b className="text-ink">{list.data ? list.data.total.toLocaleString() : '—'}</b> {list.data && list.data.total === 1 ? 'respondent' : 'respondents'}
            {chosen.length > 0 && <> · <b className="text-ink">{chosen.length}</b> selected</>}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {suspicious.length > 0 && <Button variant="ghost" size="sm" onClick={() => setSelected(new Set(suspicious.map((r) => r.id)))}>Select {suspicious.length} under 60 s</Button>}
            <Button size="sm" disabled={chosen.length === 0} onClick={() => setReconcileOpen(true)}>Change status…</Button>
          </div>
        </div>
        {body}
      </Card>

      <ReconcileDialog open={reconcileOpen} projectId={project.id} respondents={chosen} onClose={() => setReconcileOpen(false)} onDone={reconciled} />
    </div>
  );
}
